// File: src/services/api/gedcomPedi.ts
// Pure helpers for GEDCOM 5.5.1 `2 PEDI` ↔ Pecktree `relationSubtype` mapping (#1089).
//
// GEDCOM 5.5.1 standard PEDI values (under a child's `1 FAMC @Fxx@` record):
//   birth | adopted | foster | sealing
//
// Pecktree RelationTypeChild values:
//   biological | adoptive | step | foster | suspected | acknowledged
//
// Rules:
//   Import: adopted→adoptive, foster→foster, birth→biological, sealing→adoptive (LDS, conservative)
//   Export: adoptive→"adopted", foster→"foster"
//           biological/step/suspected/acknowledged → no PEDI tag (GEDCOM stays valid)

import type { RelationTypeChild } from '../../types.ts';

/** Map a raw GEDCOM PEDI value to a Pecktree RelationTypeChild.
 *  Returns undefined for unknown values (no subtype will be set on the relationship). */
export function gedcomPediToSubtype(pediValue: string): RelationTypeChild | undefined {
  switch (pediValue.trim().toLowerCase()) {
    case 'adopted':
      return 'adoptive';
    case 'foster':
      return 'foster';
    case 'birth':
      return 'biological';
    case 'sealing':
      // LDS sealing — treated conservatively as adoptive (official adoption-equivalent)
      return 'adoptive';
    default:
      // Unknown PEDI value — do not set a subtype to avoid data pollution
      return undefined;
  }
}

/** Map a Pecktree RelationTypeChild to a GEDCOM 5.5.1 PEDI value string.
 *  Returns undefined when no PEDI tag should be written (keeps export valid).
 *
 *  Only GEDCOM-standard values are ever written:
 *    adoptive → "adopted"
 *    foster   → "foster"
 *    biological / step / suspected / acknowledged → undefined
 *      (biological is GEDCOM default = omit; step/suspected/acknowledged have no
 *       GEDCOM 5.5.1 equivalent — information is preserved in-app only)
 */
export function subtypeToGedcomPedi(subtype: RelationTypeChild | undefined): string | undefined {
  switch (subtype) {
    case 'adoptive':
      return 'adopted';
    case 'foster':
      return 'foster';
    default:
      // biological = GEDCOM default (omit tag)
      // step / suspected / acknowledged = no standard GEDCOM PEDI equivalent
      return undefined;
  }
}
